"use client";

import { useState, useCallback } from "react";
import { cn } from "@/lib/utils";

type ArticleCodeBlockProps = {
  code: string;
  language?: string | null;
  className?: string;
};

const LANGUAGE_LABELS: Record<string, string> = {
  zsh: "zsh",
  bash: "bash",
  sh: "sh",
  shell: "shell",
  json: "JSON",
  tsx: "TSX",
  ts: "TS",
  jsx: "JSX",
  js: "JS",
  html: "HTML",
  css: "CSS",
};

function getTitle(lang: string | null | undefined): string {
  if (!lang) return "code";
  const normalized = lang.toLowerCase();
  return LANGUAGE_LABELS[normalized] ?? normalized;
}

export function ArticleCodeBlock({ code, language, className }: ArticleCodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }, [code]);

  const title = getTitle(language ?? undefined);

  return (
    <div
      className={cn(
        "my-6 overflow-hidden rounded-lg border border-white/10 bg-zinc-950 text-zinc-100 shadow-[0_14px_40px_rgba(0,0,0,0.22)]",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b border-white/10 bg-zinc-900/60 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
          <span className="ml-3 font-mono text-xs text-zinc-300/80">{title}</span>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="rounded px-2 py-1 font-mono text-[11px] text-zinc-400 transition-colors hover:bg-white/10 hover:text-zinc-200"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="overflow-x-auto p-5 font-mono text-[13px] leading-relaxed text-zinc-100/90">
        <code className="whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}
